/**
 * B2BAdvancedModals — Modaux additionnels pour SADA B2B
 *  - ClinicManagementModal : CRUD complet des fiches cliniques + recherche + toggle actif
 *  - CotationUploadCard    : zone d'upload PDF + visualisation status cotation
 *  - SendCotationModal     : envoi par email / WhatsApp / les deux
 *  - DeleteConfirmModal    : confirmation + motif obligatoire
 *  - TrashModal            : corbeille admin (consultation, restauration, suppression définitive)
 *
 * Tous ces modaux sont contrôlés par B2BTab.jsx (props open/onClose).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Search, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Save, Loader2,
  Upload, FileText, Send, Mail, MessageCircle, Eye, ArchiveRestore,
  AlertTriangle, RotateCcw, CheckCircle2, Building2,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { const d = new Date(iso); return d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

// ── Modal shell réutilisable ────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, size = 'lg', testId }) => {
  if (!open) return null;
  const sizes = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-6xl' };
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid={testId}>
      <div className={`w-full ${sizes[size]} bg-white rounded-xl shadow-2xl my-8`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <h3 className="text-base font-bold text-zinc-900">{title}</h3>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid={`${testId}-close`}><X className="w-5 h-5" /></Button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
// 1. CLINIC MANAGEMENT MODAL
// ════════════════════════════════════════════════════════════════════════
export const ClinicManagementModal = ({ open, onClose, canWrite, canDelete, onChanged }) => {
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [editing, setEditing] = useState(null); // clinic obj or {} for new
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/b2b/clinics`, { headers: auth(), params: { q: q || undefined, active_only: activeOnly } });
      setList(r.data);
    } catch (e) { toast.error('Erreur chargement cliniques'); }
    setLoading(false);
  }, [q, activeOnly]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const save = async (clinic) => {
    try {
      if (clinic.id) {
        await axios.patch(`${API}/b2b/clinics/${clinic.id}`, clinic, { headers: auth() });
        toast.success('Fiche clinique mise à jour');
      } else {
        await axios.post(`${API}/b2b/clinics`, clinic, { headers: auth() });
        toast.success('Fiche clinique créée');
      }
      setEditing(null);
      reload();
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur enregistrement');
    }
  };

  const toggleActive = async (id) => {
    try {
      await axios.post(`${API}/b2b/clinics/${id}/toggle-active`, {}, { headers: auth() });
      toast.success('État mis à jour');
      reload();
    } catch (e) { toast.error('Erreur'); }
  };

  const doDelete = async (clinic) => {
    const reason = prompt(`Supprimer la fiche « ${clinic.name} » — motif (optionnel) :`);
    if (reason === null) return;
    try {
      await axios.delete(`${API}/b2b/clinics/${clinic.id}`, { headers: auth(), data: { reason: reason || null } });
      toast.success('Envoyé en corbeille');
      reload();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Fiches cliniques · SADA B2B" size="xl" testId="b2b-clinic-modal">
      {editing ? (
        <ClinicForm clinic={editing} onCancel={() => setEditing(null)} onSave={save} />
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, sigle, contact, email)..." className="pl-8" data-testid="b2b-clinic-search" />
            </div>
            <label className="text-xs text-zinc-600 flex items-center gap-1">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} /> Actifs uniquement
            </label>
            {canWrite && (
              <Button onClick={() => setEditing({})} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-clinic-new">
                <Plus className="w-4 h-4 mr-1" /> Nouvelle clinique
              </Button>
            )}
          </div>
          {loading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-400" /> : (
            <div className="overflow-x-auto" data-testid="b2b-clinic-list">
              <table className="w-full text-sm">
                <thead className="text-xs text-zinc-500 uppercase border-b border-zinc-200">
                  <tr>
                    <th className="text-left py-2 px-2">Clinique</th>
                    <th className="text-left py-2 px-2">Contact</th>
                    <th className="text-left py-2 px-2">Téléphone</th>
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-left py-2 px-2">Délai pmt</th>
                    <th className="text-center py-2 px-2">État</th>
                    <th className="text-right py-2 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr><td colSpan={7} className="py-6 text-center text-zinc-400 italic">Aucune fiche clinique.</td></tr>
                  ) : list.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`b2b-clinic-row-${c.id}`}>
                      <td className="py-2 px-2">
                        <p className="font-semibold">{c.name}</p>
                        {c.short_name && <p className="text-[10px] text-zinc-400">{c.short_name}</p>}
                      </td>
                      <td className="py-2 px-2">{c.contact_principal_name || '—'}<br /><span className="text-[10px] text-zinc-400">{c.contact_principal_role || ''}</span></td>
                      <td className="py-2 px-2 tabular-nums">{c.phone_primary || '—'}</td>
                      <td className="py-2 px-2">{c.email || '—'}</td>
                      <td className="py-2 px-2 text-center">{c.payment_terms_days ? `${c.payment_terms_days}j` : '—'}</td>
                      <td className="py-2 px-2 text-center">
                        {canWrite ? (
                          <button onClick={() => toggleActive(c.id)} title={c.active ? 'Désactiver' : 'Activer'} data-testid={`b2b-clinic-toggle-${c.id}`}>
                            {c.active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-zinc-300" />}
                          </button>
                        ) : (
                          <span className={`text-[10px] ${c.active ? 'text-emerald-600' : 'text-zinc-400'}`}>{c.active ? 'Actif' : 'Inactif'}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        {canWrite && <Button size="icon" variant="ghost" onClick={() => setEditing(c)} title="Modifier" data-testid={`b2b-clinic-edit-${c.id}`}><Edit2 className="w-4 h-4" /></Button>}
                        {canDelete && <Button size="icon" variant="ghost" onClick={() => doDelete(c)} title="Supprimer" data-testid={`b2b-clinic-delete-${c.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

const ClinicForm = ({ clinic, onCancel, onSave }) => {
  const [form, setForm] = useState({
    name: '', short_name: '', code: '', contact_principal_name: '', contact_principal_role: '',
    phone_primary: '', phone_secondary: '', email: '', address: '',
    mode_default: '', channel_preferred: '', payment_terms_days: '', delivery_zone: '',
    special_conditions: '', notes: '', internal_notes: '', active: true,
    ...clinic,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.name?.trim()) { toast.error('Désignation requise'); return; }
    const payload = { ...form };
    if (payload.payment_terms_days === '' || payload.payment_terms_days === null) payload.payment_terms_days = null;
    else payload.payment_terms_days = parseInt(payload.payment_terms_days, 10);
    // Remplacement chaines vides par null pour les champs effaçables
    ['short_name','code','contact_principal_name','contact_principal_role','phone_primary','phone_secondary','email','address','mode_default','channel_preferred','delivery_zone','special_conditions','notes','internal_notes'].forEach((k) => {
      if (payload[k] === '') payload[k] = null;
    });
    onSave(payload);
  };

  return (
    <div className="space-y-3" data-testid="b2b-clinic-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Désignation *"><Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="b2b-clinic-form-name" /></Field>
        <Field label="Sigle / nom court"><Input value={form.short_name || ''} onChange={(e) => set('short_name', e.target.value)} data-testid="b2b-clinic-form-short" /></Field>
        <Field label="Code interne"><Input value={form.code || ''} onChange={(e) => set('code', e.target.value)} /></Field>
        <Field label="Contact principal"><Input value={form.contact_principal_name || ''} onChange={(e) => set('contact_principal_name', e.target.value)} /></Field>
        <Field label="Fonction du contact"><Input value={form.contact_principal_role || ''} onChange={(e) => set('contact_principal_role', e.target.value)} /></Field>
        <Field label="Téléphone principal"><Input value={form.phone_primary || ''} onChange={(e) => set('phone_primary', e.target.value)} data-testid="b2b-clinic-form-phone" /></Field>
        <Field label="Téléphone secondaire"><Input value={form.phone_secondary || ''} onChange={(e) => set('phone_secondary', e.target.value)} /></Field>
        <Field label="Email"><Input value={form.email || ''} type="email" onChange={(e) => set('email', e.target.value)} data-testid="b2b-clinic-form-email" /></Field>
        <Field label="Adresse / localisation" className="md:col-span-2"><Input value={form.address || ''} onChange={(e) => set('address', e.target.value)} /></Field>
        <Field label="Mode habituel">
          <select className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" value={form.mode_default || ''} onChange={(e) => set('mode_default', e.target.value)}>
            <option value="">—</option>
            <option value="livraison">Livraison</option>
            <option value="retrait_pharmacie">Retrait pharmacie</option>
          </select>
        </Field>
        <Field label="Canal préféré">
          <select className="w-full h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" value={form.channel_preferred || ''} onChange={(e) => set('channel_preferred', e.target.value)}>
            <option value="">—</option>
            <option value="telephone">Téléphone</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
          </select>
        </Field>
        <Field label="Délai paiement (jours)"><Input type="number" min="0" value={form.payment_terms_days ?? ''} onChange={(e) => set('payment_terms_days', e.target.value)} /></Field>
        <Field label="Zone de livraison"><Input value={form.delivery_zone || ''} onChange={(e) => set('delivery_zone', e.target.value)} /></Field>
        <Field label="Conditions particulières" className="md:col-span-2"><Textarea rows={2} value={form.special_conditions || ''} onChange={(e) => set('special_conditions', e.target.value)} /></Field>
        <Field label="Observations" className="md:col-span-2"><Textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></Field>
        <Field label="Notes internes (admin/superviseur)" className="md:col-span-2"><Textarea rows={2} value={form.internal_notes || ''} onChange={(e) => set('internal_notes', e.target.value)} /></Field>
      </div>
      <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100">
        <Button variant="outline" onClick={onCancel}>Annuler</Button>
        <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-clinic-form-save">
          <Save className="w-4 h-4 mr-1" /> Enregistrer
        </Button>
      </div>
    </div>
  );
};

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-medium text-zinc-600">{label}</Label>
    {children}
  </div>
);

// ════════════════════════════════════════════════════════════════════════
// 2. COTATION UPLOAD CARD
// ════════════════════════════════════════════════════════════════════════
export const CotationUploadCard = ({ order, canWrite, onChanged, onSendClick }) => {
  const [uploading, setUploading] = useState(false);
  const [autoSendStatus, setAutoSendStatus] = useState(true);
  const hasCotation = !!order?.cotation_storage_path || !!order?.cotation_name;
  const downloadUrl = order?.cotation_download_token
    ? `${API}/b2b/cotations/${order.id}/${order.cotation_download_token}`
    : null;

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('PDF uniquement'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('auto_send_status', autoSendStatus ? 'true' : 'false');
      await axios.post(`${API}/b2b/orders/${order.id}/cotation`, fd, {
        headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Cotation PDF téléversée');
      onChanged?.();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || 'Erreur upload');
    } finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <Card className="border-zinc-200" data-testid="b2b-cotation-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="text-sm font-bold text-zinc-700 flex items-center gap-1"><FileText className="w-4 h-4 text-violet-600" /> Cotation PDF</h3>
          {hasCotation && (
            <Button size="sm" variant="outline" onClick={onSendClick} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="b2b-cotation-send-btn">
              <Send className="w-3.5 h-3.5 mr-1" /> Envoyer au client
            </Button>
          )}
        </div>

        {hasCotation ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded-lg">
              <FileText className="w-4 h-4 text-violet-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate" title={order.cotation_name}>{order.cotation_name || 'cotation.pdf'}</p>
                <p className="text-[10px] text-zinc-500">
                  {order.cotation_size ? `${Math.round(order.cotation_size / 1024)} Ko` : ''} · Téléversé {fmtDate(order.cotation_uploaded_at)} par {order.cotation_uploaded_by || '—'}
                </p>
              </div>
              {downloadUrl && (
                <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="text-violet-700 hover:text-violet-900" data-testid="b2b-cotation-download">
                  <Eye className="w-4 h-4" />
                </a>
              )}
            </div>
            {canWrite && (
              <label className="block">
                <span className="text-[10px] text-zinc-500">Remplacer le PDF :</span>
                <input type="file" accept="application/pdf,.pdf" disabled={uploading} onChange={onPick} className="text-xs mt-1 block" data-testid="b2b-cotation-replace" />
              </label>
            )}
          </div>
        ) : canWrite ? (
          <div className="space-y-2">
            <label className="block p-4 border-2 border-dashed border-zinc-300 rounded-lg text-center hover:border-violet-400 transition cursor-pointer">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-400" /> : (
                <>
                  <Upload className="w-6 h-6 mx-auto text-zinc-400 mb-1" />
                  <p className="text-xs text-zinc-600">Cliquer pour téléverser un PDF de cotation (max 5 Mo)</p>
                </>
              )}
              <input type="file" accept="application/pdf,.pdf" disabled={uploading} onChange={onPick} className="hidden" data-testid="b2b-cotation-upload" />
            </label>
            <label className="flex items-center gap-1 text-[11px] text-zinc-600">
              <input type="checkbox" checked={autoSendStatus} onChange={(e) => setAutoSendStatus(e.target.checked)} />
              Passer automatiquement au statut « Cotation envoyée » après upload
            </label>
          </div>
        ) : (
          <p className="text-xs text-zinc-400 italic">Aucune cotation PDF dans ce dossier.</p>
        )}
      </CardContent>
    </Card>
  );
};

// ════════════════════════════════════════════════════════════════════════
// 3. SEND COTATION MODAL
// ════════════════════════════════════════════════════════════════════════
export const SendCotationModal = ({ open, onClose, order, clinics, onSent }) => {
  const clinic = clinics?.find((c) => c.id === order?.clinic_id);
  const [channel, setChannel] = useState('both');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(clinic?.email || '');
      setPhone(clinic?.phone_primary || order?.phone || '');
      setMessage('');
      setChannel(clinic?.channel_preferred === 'email' ? 'email' : clinic?.channel_preferred === 'whatsapp' ? 'whatsapp' : 'both');
    }
  }, [open, clinic, order]);

  const send = async () => {
    if (channel !== 'whatsapp' && !email.trim()) { toast.error('Email destinataire requis'); return; }
    if (channel !== 'email' && !phone.trim()) { toast.error('Numéro WhatsApp requis'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/b2b/orders/${order.id}/send-cotation`,
        { channel, email: email || null, phone: phone || null, message: message || null },
        { headers: auth() });
      if (r.data.errors?.length) toast.warning(r.data.errors.join(' · '));
      if (r.data.email_sent) toast.success('Email envoyé');
      if (r.data.whatsapp_url) {
        toast.success('Lien WhatsApp prêt');
        window.open(r.data.whatsapp_url, '_blank', 'noopener,noreferrer');
      }
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally { setSending(false); }
  };

  if (!order) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Envoyer la cotation · ${order?.clinic_name || ''}`} size="md" testId="b2b-send-cotation-modal">
      <div className="space-y-3">
        <div>
          <Label className="text-xs font-medium text-zinc-600">Canal d&apos;envoi</Label>
          <div className="flex gap-2 mt-1" data-testid="b2b-send-channel-picker">
            {[
              { id: 'email', label: 'Email', icon: Mail },
              { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
              { id: 'both', label: 'Les deux', icon: Send },
            ].map((opt) => {
              const I = opt.icon;
              return (
                <button key={opt.id} onClick={() => setChannel(opt.id)}
                  className={`flex-1 px-3 py-2 rounded-md border text-sm flex items-center justify-center gap-1
                    ${channel === opt.id ? 'border-violet-500 bg-violet-50 text-violet-800 font-semibold' : 'border-zinc-200 hover:border-zinc-300'}`}
                  data-testid={`b2b-send-channel-${opt.id}`}>
                  <I className="w-4 h-4" /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {channel !== 'whatsapp' && (
          <Field label="Email destinataire">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@clinique.com" data-testid="b2b-send-email" />
            <p className="text-[10px] text-zinc-400 mt-0.5">Par défaut : email de la fiche clinique.</p>
          </Field>
        )}
        {channel !== 'email' && (
          <Field label="Numéro WhatsApp">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07 07 07 07 07" data-testid="b2b-send-phone" />
            <p className="text-[10px] text-zinc-400 mt-0.5">Code +225 ajouté automatiquement si format local.</p>
          </Field>
        )}
        <Field label="Message personnalisé (optionnel)">
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Si vide : message standard avec lien PDF." data-testid="b2b-send-message" />
        </Field>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <Button variant="outline" onClick={onClose} disabled={sending}>Annuler</Button>
          <Button onClick={send} disabled={sending} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="b2b-send-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Envoyer
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ════════════════════════════════════════════════════════════════════════
// 4. DELETE CONFIRM MODAL
// ════════════════════════════════════════════════════════════════════════
export const DeleteConfirmModal = ({ open, onClose, title, body, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);

  const confirm = async () => {
    setLoading(true);
    try { await onConfirm(reason || null); onClose(); }
    catch (e) {/* toast handled upstream */}
    finally { setLoading(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={title || 'Confirmer la suppression'} size="sm" testId="b2b-delete-modal">
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />
          <p className="text-xs text-amber-900">{body || 'L\'élément sera envoyé en corbeille. Il pourra être restauré par un administrateur.'}</p>
        </div>
        <Field label="Motif de la suppression (optionnel mais recommandé)">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="b2b-delete-reason" />
        </Field>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
          <Button variant="outline" onClick={onClose} disabled={loading}>Annuler</Button>
          <Button onClick={confirm} disabled={loading} className="bg-red-600 hover:bg-red-700 text-white" data-testid="b2b-delete-confirm">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />} Envoyer en corbeille
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ════════════════════════════════════════════════════════════════════════
// 5. TRASH MODAL (admin only)
// ════════════════════════════════════════════════════════════════════════
export const TrashModal = ({ open, onClose, onRestored }) => {
  const [data, setData] = useState({ orders: [], clinics: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('orders');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/b2b/trash`, { headers: auth() });
      setData(r.data);
    } catch (e) { toast.error('Erreur chargement corbeille'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const restore = async (kind, id) => {
    if (!window.confirm('Restaurer cet élément ?')) return;
    try {
      await axios.post(`${API}/b2b/trash/${kind}/${id}/restore`, {}, { headers: auth() });
      toast.success('Restauré');
      reload();
      onRestored?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const hardDelete = async (kind, id, label) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT « ${label} » ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/b2b/trash/${kind}/${id}`, { headers: auth() });
      toast.success('Supprimé définitivement');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const items = data[tab] || [];
  return (
    <Modal open={open} onClose={onClose} title="🗑️ Corbeille B2B · Admin uniquement" size="xl" testId="b2b-trash-modal">
      <div className="flex gap-1 mb-3 border-b border-zinc-200">
        {[
          { id: 'orders', label: `Dossiers (${data.orders?.length || 0})` },
          { id: 'clinics', label: `Cliniques (${data.clinics?.length || 0})` },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === t.id ? 'border-violet-600 text-violet-700 font-semibold' : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
            data-testid={`b2b-trash-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {loading ? <Loader2 className="w-5 h-5 mx-auto animate-spin text-zinc-400" /> : items.length === 0 ? (
        <p className="text-center text-zinc-400 italic py-6">Corbeille vide pour cette catégorie.</p>
      ) : (
        <div className="overflow-x-auto" data-testid={`b2b-trash-list-${tab}`}>
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase border-b border-zinc-200">
              <tr>
                <th className="text-left py-2 px-2">Référence</th>
                <th className="text-left py-2 px-2">Supprimé le</th>
                <th className="text-left py-2 px-2">Par</th>
                <th className="text-left py-2 px-2">Motif</th>
                <th className="text-right py-2 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-zinc-100" data-testid={`b2b-trash-row-${it.id}`}>
                  <td className="py-2 px-2">
                    <p className="font-medium">{tab === 'orders' ? `${it.clinic_name || '—'} · ${(it.items || []).length} ligne(s)` : it.name}</p>
                    <p className="text-[10px] text-zinc-400 truncate max-w-[300px]">ID {it.id?.slice(0, 8)}</p>
                  </td>
                  <td className="py-2 px-2 text-xs">{fmtDate(it.deleted_at)}</td>
                  <td className="py-2 px-2 text-xs">{it.deleted_by_name || '—'}</td>
                  <td className="py-2 px-2 text-xs italic text-zinc-600 max-w-[200px] truncate" title={it.delete_reason}>{it.delete_reason || '—'}</td>
                  <td className="py-2 px-2 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => restore(tab, it.id)} title="Restaurer" data-testid={`b2b-trash-restore-${it.id}`}>
                      <RotateCcw className="w-4 h-4 text-emerald-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => hardDelete(tab, it.id, it.clinic_name || it.name)} title="Supprimer définitivement" data-testid={`b2b-trash-hard-${it.id}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};
